import { useEffect, useState } from "react";
import { getAdminReturnTo, clearAdminReturnTo } from "../utils/adminReturnTo";
import Backend from "../utils/api.js";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import { useAppToast } from "../Components/AppToast.jsx";
import useVersion from "../hooks/useVersion.jsx";
import pinpointLogo from "../assets/pinpoint_no_bg.svg";

function LoginCAS({ onSubmit, history }) {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useAppToast();
  const nextRedirect = getAdminReturnTo() || '/profile';
  const { version, environment } = useVersion();

  useDocumentTitle("Log In With CAS – Pinpoint");
  useEffect(() => {
    const callbackUrl = new URL(window.location.href);
    const params = callbackUrl.searchParams;

    // In the case that ticket is not null (i.e. we have a session), this is a callback from a CAS login
    const ticket = params.get('ticket');
    if (ticket) {
      // CAS tickets are single-use. Consume the URL parameter before any async
      // work so remounts, Fast Refresh, and reloads cannot submit it again.
      params.delete('ticket');
      window.history.replaceState(window.history.state, '', callbackUrl.toString());
      setIsLoading(true);

      Backend.casLoginCallback(ticket, nextRedirect).then((res) => {
        setIsLoading(false);

        // This redirect is where the user will go after a successful cas login.
        if (res.redirect) {
          Backend.getUser(res.user).then((user_res) => {
            if (user_res.error) {
              toast({
                title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
                status: "error",
                duration: 8000,
              });
            // login exists in pinpoint
            } else if (user_res.status === 200) {
              clearAdminReturnTo();
              history.replace(nextRedirect);
              onSubmit(user_res);
            // new user: send to registration (same flow as kiosk).
            // Navigate FIRST, then mark the app "logged in": onSubmit's
            // setUser (a setState fired outside a React event handler) flushes
            // synchronously in React 17, so if it ran first, the still-current
            // "/caslogin" route's `user.UID ? <Redirect to="/profile"/> : ...`
            // check would briefly redirect to /profile for a user with no DB
            // row yet — Profile's data calls 404 and fire a spurious
            // "Server call failed" toast before this replace() corrects the
            // URL. RegisterForm reads the UID from location.state regardless,
            // so onSubmit here only needs to run before the user interacts
            // with the form, not before the navigation.
            } else {
              history.replace("/register", { UID: res.user });
              onSubmit({ UID: res.user });
            }
          }).catch(() => {
            setIsLoading(false);
            toast({
              title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
              status: "error",
              duration: 8000,
            });
          });
        } else if (res.error === 'network_error') {
          toast({
            title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
            status: "error",
            duration: 8000,
          });
        } else if (typeof res.error === 'string' && (
          /ldap/i.test(res.error) || res.error.startsWith('Authentication error:')
        )) {
          // Directory lookup failures can omit "LDAP" (e.g. invalid bind credentials).
          toast({
            title: "Pinpoint is having an issue connecting to the campus directory. Please try again in a few minutes. If the issue persists, please email the Engineering Digital Service via eds@umd.edu.",
            status: "error",
            duration: 8000,
          });
        } else {
          toast({
            title: "Pinpoint was unable to verify your CAS login. Please clear your browser cache & cookies, wait a few minutes, and then try again. If the issue persists, please email the Engineering Digital Service via eds@umd.edu.",
            status: "error",
            duration: 8000,
          });
        }
      }).catch(() => {
        setIsLoading(false);
        toast({
          title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
          status: "error",
          duration: 8000,
        });
      });
    }
  }, []);

  function login(e) {
    e.preventDefault();
    setIsLoading(true);

    Backend.casLogin().then((res) => {
      setIsLoading(false)

      if (res.redirect) {
        // Don't change this redirect! This is where the user is sent to auth with CAS
        window.location.replace(res.redirect);
      } else {
        toast({
          title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
          status: "error",
          duration: 8000,
        });
      }
    }).catch(() => {
      setIsLoading(false);
      toast({
        title: "Pinpoint is having issues right now. Please try again in a few seconds. If this error persists, contact eds@umd.edu.",
        status: "error",
        duration: 8000,
      });
    });
  }

  return(
    <Flex
      minH={"100vh"}
      align={"center"}
      justify={"center"}
      bg={useColorModeValue("gray.50", "gray.800")}
    >
      <Stack spacing={10} mx={"auto"} maxW={"md"} py={12} px={6}>
        <Stack align={"center"} spacing={4}>
          <Image
            width="5em"
            src={pinpointLogo}
            alt="pinpoint logo"
          />
          <Heading fontSize={"5xl"} fontWeight="bold" color="gray.800">
            Pinpoint
          </Heading>
        </Stack>
        <Box
          rounded={"lg"}
          bg={useColorModeValue("white", "gray.700")}
          boxShadow={"md"}
          p={8}
        >
          <Text fontSize={"md"} color={"gray.600"} align={"center"} lineHeight="tall">
            Welcome to Pinpoint! Log in to manage your profile and connect your account to Parchment Digital Badges.
          </Text>
          <form onSubmit={login}>
            <Button
              loadingText="Logging In..."
              isLoading={isLoading}
              type="submit"
              w="full"
              mt={6}
            >
              Log in with UMD CAS
            </Button>
          </form>
        </Box>
        <Flex
          fontSize={"sm"}
          color={"gray.500"}
          justify={"center"}
          wrap="wrap"
          gap={1}
          rowGap={0}
        >
          <Link href="https://docs.pinpoint.umd.edu" isExternal>Documentation</Link>
          <Text as="span" color="gray.300">&middot;</Text>
          <Link href="https://features.pinpoint.umd.edu" isExternal>Features</Link>
          <Text as="span" color="gray.300">&middot;</Text>
          <Link href="https://umd.edu/privacy">Privacy</Link>
          <Text as="span" color="gray.300">&middot;</Text>
          <Link href="https://umd.edu/web-accessibility">Web Accessibility</Link>
        </Flex>
        <Text fontSize="xs" color="gray.400" textAlign="center">
          &copy; {new Date().getFullYear()} University of Maryland
          {version && environment && (
            <>
              <br />
              Pinpoint v{version} &middot; {environment}
            </>
          )}
        </Text>
      </Stack>
    </Flex>
  );
}

LoginCAS.propTypes = {
  onSubmit: PropTypes.func,
};

export default withRouter(LoginCAS)
